import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from './config';
import { wvConfirm } from './Confirm';

// THE THREAD SHARING PANEL (2026-08-13) — replaces the bare publish block.
//
// One surface for the whole audience question: who can read this now, share to
// everyone, share to a group, secret links, and unpublish. Every disclosure
// keeps the TWO-CALL shape the publish endpoint rules (audit first, changing
// nothing; confirm second) — collapsing them into one button is the failure
// that design refuses, and it holds for every audience here, including links.
//
// The STALENESS line is the panel's load-bearing part: steps appended after a
// share mint no visibility edge of their own (appending never auto-shares), so
// a shared thread accumulates members its audience cannot see, silently.
// `unshared_members` from GET /api/threads/:id/sharing is that count, and
// re-sharing clears it.

const snip = (s, n) => (s && s.length > n ? `${s.slice(0, n)}…` : s || '');

function AuditBlock({ audit, verb, onConfirm, onCancel, busy }) {
  const members = audit.private_members || audit.would_share || [];
  const owned = members.filter((m) => m.owned !== false);
  const others = members.filter((m) => m.owned === false);
  return (
    <div style={{ marginTop: 'var(--wv-sp-2)' }}>
      <p style={{ marginBottom: 'var(--wv-sp-2)', fontSize: 'var(--wv-fs-sm)' }}>{audit.note}</p>
      {owned.length > 0 && (
        <ul style={{ marginBottom: 'var(--wv-sp-2)' }}>
          {owned.map((m) => (
            <li key={m.id} style={{ fontSize: 'var(--wv-fs-sm)' }}>{snip(m.content, 90)}</li>
          ))}
        </ul>
      )}
      {others.length > 0 && (
        <>
          <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', marginBottom: 'var(--wv-sp-1)' }}>
            These are someone else’s — sharing them wider is a call you are making on their content:
          </p>
          <ul style={{ marginBottom: 'var(--wv-sp-2)' }}>
            {others.map((m) => (
              <li key={m.id} style={{ fontSize: 'var(--wv-fs-sm)' }}>{snip(m.content, 90)}</li>
            ))}
          </ul>
        </>
      )}
      {audit.withheld > 0 && (
        <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>
          {audit.withheld} step{audit.withheld === 1 ? '' : 's'} belong to someone else and are not
          yours to share — readers will see the thread end early.
        </p>
      )}
      {audit.nested_threads?.length > 0 && (
        <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>
          Contains {audit.nested_threads.length} nested thread
          {audit.nested_threads.length === 1 ? '' : 's'} — each chapter needs its own share, or its
          readers land on an empty page.
        </p>
      )}
      <button className="wv-chip" disabled={busy} onClick={onConfirm}>{verb}</button>{' '}
      <button className="wv-chip" disabled={busy} onClick={onCancel}>cancel</button>
    </div>
  );
}

export default function ThreadShare({ threadId, published, onChanged }) {
  const [sharing, setSharing] = useState(null);
  const [myGroups, setMyGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // { kind, audit, groups?, label? }
  const [mintedUrl, setMintedUrl] = useState(null); // shown ONCE
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [grants, setGrants] = useState(null); // { grants, onward_shares }

  const loadSharing = useCallback(() => {
    apiFetch(`/api/threads/${threadId}/sharing`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('failed'))))
      .then(setSharing)
      .catch(() => setError('Could not load sharing state'));
  }, [threadId]);

  // Grants hang off the OBJECT door, not the thread door: a grant is per
  // object and a thread is one (2026-08-16). The same call serves a claim.
  const loadGrants = useCallback(() => {
    apiFetch(`/api/objects/${threadId}/grants`)
      .then((r) => (r.ok ? r.json() : null))
      .then((g) => g && setGrants(g))
      .catch(() => {});
  }, [threadId]);

  useEffect(() => {
    if (open) {
      loadSharing();
      loadGrants();
      apiFetch('/api/groups')
        .then((r) => (r.ok ? r.json() : []))
        .then((g) => setMyGroups(Array.isArray(g) ? g : []))
        .catch(() => {});
    }
  }, [open, loadSharing, loadGrants]);

  const grantTo = async (granteeObjectId) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/objects/${threadId}/grants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grantee_object_id: granteeObjectId }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'failed');
      loadGrants();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const revokeGrant = async (grant) => {
    const who = grant.grantee_type === 'group' ? `the group “${grant.grantee_name}”` : grant.grantee_name;
    if (!(await wvConfirm(`Stop letting ${who} pass this on?`, { confirmLabel: 'Take it back' }))) return;
    // The unpublish ceremony, for the same reason: ending a capability and
    // undoing what was done with it are different acts, and the second one
    // narrows someone's access.
    const alsoRevert = await wvConfirm(
      'Also take back what they already shared? Anything they passed on stays shared otherwise.',
      { confirmLabel: 'Take those back too', cancelLabel: 'Leave them shared' }
    );
    setBusy(true);
    try {
      const res = await apiFetch(`/api/objects/${threadId}/grants/${grant.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ revert_shares: alsoRevert }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'failed');
      loadGrants();
      loadSharing();
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const call = async (path, body) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'failed');
      return json;
    } finally {
      setBusy(false);
    }
  };

  const startPublicShare = async () => {
    try {
      const audit = await call(`/api/threads/${threadId}/publish`, {});
      setPending({ kind: 'public', audit });
    } catch (e) { setError(e.message); }
  };

  const startGroupShare = async (groupObjectIds) => {
    try {
      const audit = await call(`/api/threads/${threadId}/publish`, {
        audience: { human: 'group', groups: groupObjectIds },
      });
      setPending({ kind: 'group', audit, groups: groupObjectIds });
    } catch (e) { setError(e.message); }
  };

  const startLink = async (label) => {
    try {
      const audit = await call(`/api/threads/${threadId}/links`, { label });
      setPending({ kind: 'link', audit, label });
    } catch (e) { setError(e.message); }
  };

  const confirmPending = async () => {
    try {
      if (pending.kind === 'public') {
        await call(`/api/threads/${threadId}/publish`, { share_members: true });
      } else if (pending.kind === 'group') {
        await call(`/api/threads/${threadId}/publish`, {
          audience: { human: 'group', groups: pending.groups },
          share_members: true,
        });
      } else if (pending.kind === 'link') {
        const made = await call(`/api/threads/${threadId}/links`, { label: pending.label, confirm: true });
        setMintedUrl(`${window.location.origin}${made.url}`);
      }
      setPending(null);
      loadSharing();
      onChanged();
    } catch (e) { setError(e.message); }
  };

  const unpublish = async (target, targetLabel) => {
    const revert = await wvConfirm(
      `Stop sharing with ${targetLabel}? Member objects this share made visible will STAY visible unless you also take them back.`,
      { confirmLabel: 'Stop sharing', cancelLabel: 'Keep sharing' }
    );
    if (!revert) return;
    const alsoMembers = await wvConfirm(
      'Also take back the member objects this share made visible? Shares that existed before it are never touched.',
      { confirmLabel: 'Take members back too', cancelLabel: 'Leave members shared' }
    );
    try {
      await call(`/api/threads/${threadId}/unpublish`, {
        target,
        confirm: true,
        revert_members: alsoMembers,
      });
      loadSharing();
      onChanged();
    } catch (e) { setError(e.message); }
  };

  const revokeLink = async (linkId) => {
    if (!(await wvConfirm('Revoke this link? Anyone holding the URL loses access on their next visit.', { confirmLabel: 'Revoke' }))) return;
    try {
      setBusy(true);
      const res = await apiFetch(`/api/threads/${threadId}/links/${linkId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'failed');
      loadSharing();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button className="wv-chip" onClick={() => setOpen(true)}>
        {published ? 'sharing…' : 'share this thread…'}
      </button>
    );
  }

  const liveLinks = (sharing?.audience?.links || []).filter((l) => !l.revoked_at);
  const audienceEmpty =
    sharing && !sharing.audience.public && sharing.audience.groups.length === 0 && liveLinks.length === 0
    && (sharing.audience.other_targets || []).length === 0;

  return (
    <div style={{ marginTop: 'var(--wv-sp-2)' }}>
      <button className="wv-chip" onClick={() => setOpen(false)}>close sharing</button>
      {error && <div style={{ color: 'var(--wv-neg)', fontSize: 'var(--wv-fs-sm)', marginTop: 'var(--wv-sp-1)' }}>{error}</div>}
      {!sharing && !error && <p className="trust-muted">Loading…</p>}

      {mintedUrl && (
        <div className="wv-panel" style={{ marginTop: 'var(--wv-sp-2)', padding: 'var(--wv-sp-2)', borderColor: 'var(--wv-pos)' }}>
          <div style={{ fontSize: 'var(--wv-fs-sm)', marginBottom: 'var(--wv-sp-1)' }}>
            Copy this link now — it cannot be shown again:
          </div>
          <div style={{ display: 'flex', gap: 'var(--wv-sp-1)', alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={{ fontSize: 'var(--wv-fs-xs)', wordBreak: 'break-all', flex: 1, minWidth: '240px' }}>{mintedUrl}</code>
            <button
              className="wv-chip"
              onClick={async () => {
                try { await navigator.clipboard.writeText(mintedUrl); setCopied(true); } catch {}
              }}
            >
              {copied ? '✓ copied' : 'copy'}
            </button>
          </div>
        </div>
      )}

      {sharing && (
        <div style={{ marginTop: 'var(--wv-sp-2)', display: 'flex', flexDirection: 'column', gap: 'var(--wv-sp-2)' }}>
          {/* ——— who can read this now ——— */}
          <div style={{ display: 'flex', gap: 'var(--wv-sp-1)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>Audience:</span>
            {audienceEmpty && <span className="wv-chip">only you</span>}
            {sharing.audience.public && (
              <span className="wv-chip">
                everyone{' '}
                <button
                  disabled={busy}
                  onClick={() => unpublish('public', 'everyone')}
                  style={{ fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-neg)', border: 'none', background: 'none', cursor: 'pointer' }}
                >
                  ×
                </button>
              </span>
            )}
            {sharing.audience.groups.map((g) => (
              <span key={g.object_id} className="wv-chip">
                {g.name}{' '}
                <button
                  disabled={busy}
                  onClick={() => unpublish(g.object_id, `the group “${g.name}”`)}
                  style={{ fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-neg)', border: 'none', background: 'none', cursor: 'pointer' }}
                >
                  ×
                </button>
              </span>
            ))}
            {liveLinks.map((l) => (
              <span key={l.id} className="wv-chip" title={l.last_used_at ? `last used ${new Date(l.last_used_at).toLocaleString()}` : 'never used'}>
                🔗 {l.label || 'secret link'}{' '}
                <button
                  disabled={busy}
                  onClick={() => revokeLink(l.id)}
                  style={{ fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-neg)', border: 'none', background: 'none', cursor: 'pointer' }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          {/* ——— staleness ——— */}
          {!audienceEmpty && sharing.unshared_members > 0 && (
            <div className="wv-panel" style={{ padding: 'var(--wv-sp-2)', borderColor: 'var(--wv-neg)' }}>
              <span style={{ fontSize: 'var(--wv-fs-sm)' }}>
                {sharing.unshared_members} step{sharing.unshared_members === 1 ? 's' : 's'} added since you shared{' '}
                {sharing.unshared_members === 1 ? 'is' : 'are'} not visible to your audience.
              </span>{' '}
              {(sharing.audience.public || sharing.audience.groups.length > 0) && (
                <button
                  className="wv-chip"
                  disabled={busy}
                  onClick={() =>
                    sharing.audience.public
                      ? startPublicShare()
                      : startGroupShare(sharing.audience.groups.map((g) => g.object_id))
                  }
                >
                  review and re-share…
                </button>
              )}
              {!sharing.audience.public && sharing.audience.groups.length === 0 && liveLinks.length > 0 && (
                <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>
                  For a link audience, revoke the link and mint a fresh one.
                </span>
              )}
            </div>
          )}

          {/* ——— pending audit ——— */}
          {pending && (
            <AuditBlock
              audit={pending.audit}
              verb={pending.kind === 'link' ? 'mint the link' : pending.kind === 'group' ? 'share with the group' : 'publish to everyone'}
              onConfirm={confirmPending}
              onCancel={() => setPending(null)}
              busy={busy}
            />
          )}

          {/* ——— the three doors ——— */}
          {!pending && (
            <div style={{ display: 'flex', gap: 'var(--wv-sp-1)', flexWrap: 'wrap', alignItems: 'center' }}>
              {!sharing.audience.public && (
                <button className="wv-chip" disabled={busy} onClick={startPublicShare}>
                  share with everyone…
                </button>
              )}
              {sharing.audience.public ? (
                <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>
                  Already public — everyone can read this.
                </span>
              ) : (
                myGroups
                  .filter((g) => !sharing.audience.groups.some((sg) => sg.object_id === g.object_id))
                  .map((g) => (
                    <button key={g.object_id} className="wv-chip" disabled={busy} onClick={() => startGroupShare([g.object_id])}>
                      share with {g.name}…
                    </button>
                  ))
              )}
              <button
                className="wv-chip"
                disabled={busy}
                onClick={() => {
                  const label = window.prompt('Label for this link (optional — e.g. who it is for):') ?? null;
                  if (label !== null) startLink(label);
                }}
              >
                secret link…
              </button>
            </div>
          )}

          {/* ——— who may pass this on (2026-08-16) ———
              The author's half of the onward-sharing grant. Kept below the
              three doors because it is a rarer act than sharing, and above the
              model note because it is about people. */}
          {!pending && (
            <div style={{ borderTop: '1px solid var(--wv-border)', paddingTop: 'var(--wv-sp-2)' }}>
              <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', marginBottom: 'var(--wv-sp-1)' }}>
                Who may pass this on:
              </div>
              <div style={{ display: 'flex', gap: 'var(--wv-sp-1)', flexWrap: 'wrap', alignItems: 'center' }}>
                {(grants?.grants || []).length === 0 && (
                  <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>nobody but you</span>
                )}
                {(grants?.grants || []).map((g) => (
                  <span key={g.id} className="wv-chip">
                    {g.grantee_type === 'group' ? `${g.grantee_name} (${g.member_count})` : g.grantee_name}{' '}
                    <button
                      disabled={busy}
                      onClick={() => revokeGrant(g)}
                      style={{ fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-neg)', border: 'none', background: 'none', cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 'var(--wv-sp-1)', flexWrap: 'wrap', marginTop: 'var(--wv-sp-1)' }}>
                {myGroups
                  .filter((g) => !(grants?.grants || []).some((gr) => gr.grantee_object_id === g.object_id))
                  .map((g) => (
                    <button key={g.object_id} className="wv-chip" disabled={busy} onClick={() => grantTo(g.object_id)}>
                      let {g.name} pass this on
                    </button>
                  ))}
              </div>
              {(grants?.grants || []).some((g) => g.grantee_type === 'group') && (
                <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-1)' }}>
                  Group grants are live: anyone you add to that group can pass this on, without you
                  acting again. They can share to their own groups or to a person — never to
                  everyone — and the people they share with cannot pass it on further.
                </p>
              )}
              {(grants?.onward_shares || []).length > 0 && (
                <div style={{ marginTop: 'var(--wv-sp-2)' }}>
                  <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>Passed on by:</div>
                  <ul style={{ marginTop: 'var(--wv-sp-1)' }}>
                    {grants.onward_shares.map((s) => (
                      <li key={s.id} style={{ fontSize: 'var(--wv-fs-sm)' }}>
                        {s.actor_name} → {s.audience_name}
                        {s.reverted_at ? ' (taken back)' : ''}{' '}
                        <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>
                          {new Date(s.created_at).toLocaleDateString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Chapters are separate objects, so a grant here does not reach
                  them. Said out loud, because "I shared the book" is exactly
                  the assumption that leaves every chapter unreachable. */}
              {sharing.nested_threads?.length > 0 && (
                <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-1)' }}>
                  This grant covers this thread only — each chapter needs its own.
                </p>
              )}
            </div>
          )}

          {sharing.ai_visible === false && (
            <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>
              This thread is sealed to models, and sharing does not change that.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
